import { useState, useEffect, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { parseISO } from "date-fns";
import { toast } from "sonner";
import type { OrderFormData, OrderLine, Configuration, ConfigComponent, OrderTrackingInfo, ConfigAttribute } from "../types";
import type { WorkDetails } from "../../WorkDetailsForm";
import type { 
  ConfigurationLoadResult, 
  ComponentTypeMaterialWithName,
  ConfigComponentQueryResult,
  ConfigAttributeValueQueryResult,
  AttributeDefWithAttribute,
  CoordinatesValue
} from "@/types/supabase-helpers";

interface UseLoadOrderProps {
  open: boolean;
  orderId?: string;
  form: UseFormReturn<OrderFormData>;
  setOrderLines: React.Dispatch<React.SetStateAction<OrderLine[]>>;
  setConfiguration: React.Dispatch<React.SetStateAction<Configuration | null>>;
  setConfigurationLineId: React.Dispatch<React.SetStateAction<string | null>>;
}

interface UseLoadOrderResult {
  isLoadingOrder: boolean;
  orderTrackingInfo: OrderTrackingInfo | null;
  originalStatus: string | null;
  resetLoadOrderState: () => void;
}

export function useLoadOrder({
  open,
  orderId,
  form,
  setOrderLines,
  setConfiguration,
  setConfigurationLineId,
}: UseLoadOrderProps): UseLoadOrderResult {
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [orderTrackingInfo, setOrderTrackingInfo] = useState<OrderTrackingInfo | null>(null);
  const [originalStatus, setOriginalStatus] = useState<string | null>(null);

  const resetLoadOrderState = useCallback(() => {
    setOrderTrackingInfo(null);
    setOriginalStatus(null);
  }, []);

  useEffect(() => {
    if (open && orderId) {
      setIsLoadingOrder(true);

      const loadOrder = async () => {
        try {
          // Load order data
          const { data: orderData, error: orderError } = await supabase
            .from("orders")
            .select("*")
            .eq("id", orderId)
            .single();

          if (orderError) throw orderError;

          // Store tracking info
          if (orderData.order_number && orderData.tracking_token) {
            setOrderTrackingInfo({
              order_number: orderData.order_number,
              tracking_token: orderData.tracking_token,
            });
          }

          setOriginalStatus(orderData.status);

          // Load order lines
          const { data: linesData, error: linesError } = await supabase
            .from("order_lines")
            .select("*")
            .eq("order_id", orderId)
            .order("position");

          if (linesError) throw linesError;

          // Check for configuration
          const lineWithConfig = linesData.find((line) => line.configuration_id);

          if (lineWithConfig?.configuration_id) {
            await loadConfiguration(lineWithConfig.configuration_id, lineWithConfig.id);
          }

          // Populate form
          form.reset({
            customer_id: orderData.customer_id,
            partner_id: orderData.partner_id || undefined,
            cemetery_id: orderData.cemetery_id || undefined,
            order_date: parseISO(orderData.order_date),
            installation_date: orderData.installation_date ? parseISO(orderData.installation_date) : undefined,
            delivery_date: orderData.delivery_date ? parseISO(orderData.delivery_date) : undefined,
            order_type: (orderData.order_type || "new_work") as "new_work" | "pickup_work" | "field_work",
            fulfillment_type: orderData.fulfillment_type,
            status: orderData.status,
            notes: orderData.notes || undefined,
            commission_type: orderData.commission_type || undefined,
            commission_value: orderData.commission_value || undefined,
          });

          // Populate order lines
          setOrderLines(
            linesData.map((line) => ({
              id: line.id,
              product_id: line.product_id,
              configuration_id: line.configuration_id,
              description: line.description,
              quantity: line.quantity,
              unit_price: line.unit_price,
              vat_rate: line.vat_rate,
              line_total: line.line_total,
              price_includes_vat: line.price_includes_vat ?? false,
              work_details: line.work_details as WorkDetails | null,
            }))
          );
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Tuntematon virhe";
          toast.error("Virhe tilauksen lataamisessa", { description: message });
        } finally {
          setIsLoadingOrder(false);
        }
      };

      const loadConfiguration = async (configId: string, lineId: string) => {
        const { data: configData, error: configError } = await supabase
          .from("configurations")
          .select(`id, name, product_model_id, product_models (id, name, is_custom_template)`)
          .eq("id", configId)
          .single();

        if (configError || !configData) return;

        const { data: configComponents } = await supabase
          .from("config_components")
          .select(`
            id, model_component_id, material_id, quantity, width, height, thickness, source_model_name,
            model_components (id, component_type_id, min_quantity, max_quantity, label,
              component_types (id, name, label))
          `)
          .eq("configuration_id", configData.id);

        const { data: attrValues } = await supabase
          .from("config_attribute_values")
          .select(`
            id, config_component_id, attribute_id, value_text, value_number, value_date, value_json,
            attributes (id, name, label, attribute_type, options, is_required)
          `)
          .in("config_component_id", configComponents?.map((c) => c.id) || []);

        const componentTypeIds = (configComponents as ConfigComponentQueryResult[] | undefined)?.map((c) => c.model_components?.component_type_id).filter((id): id is string => !!id) || [];

        const { data: materialsData } = await supabase
          .from("component_type_materials")
          .select(`component_type_id, material_id, is_default, materials (id, name)`)
          .in("component_type_id", componentTypeIds);

        const { data: attributeDefsData } = await supabase
          .from("component_attribute_defs")
          .select(`id, component_type_id, attribute_id, is_required, default_value,
            attributes (id, name, label, attribute_type, options, is_required)`)
          .in("component_type_id", componentTypeIds);

        const loadedComponents: ConfigComponent[] = ((configComponents || []) as ConfigComponentQueryResult[]).map((cc) => {
          const modelComp = cc.model_components;
          const componentType = modelComp?.component_types;
          const componentTypeId = modelComp?.component_type_id;

          const allowedMaterials = ((materialsData || []) as ComponentTypeMaterialWithName[])
            .filter((m) => m.component_type_id === componentTypeId)
            .map((m) => ({
              id: m.material_id,
              name: m.materials?.name || "Tuntematon",
              isDefault: m.is_default,
            }));

          const componentAttrValues = ((attrValues || []) as ConfigAttributeValueQueryResult[]).filter((av) => av.config_component_id === cc.id);

          const attrDefs = ((attributeDefsData || []) as AttributeDefWithAttribute[])
            .filter((ad) => ad.component_type_id === componentTypeId)
            .map((ad) => {
              const attr = ad.attributes;
              const savedValue = componentAttrValues.find((av) => av.attribute_id === ad.attribute_id);

              let value: string | number | CoordinatesValue | null = null;
              if (savedValue) {
                if (attr?.attribute_type === "number") {
                  value = savedValue.value_number;
                } else if (attr?.attribute_type === "date") {
                  value = savedValue.value_date;
                } else if (attr?.attribute_type === "coordinates" && savedValue.value_json) {
                  // Use type guard for coordinates - cast through unknown for Json compatibility
                  const jsonValue = savedValue.value_json as unknown;
                  if (typeof jsonValue === "object" && jsonValue !== null && "x" in jsonValue && "y" in jsonValue) {
                    value = jsonValue as CoordinatesValue;
                  }
                } else {
                  value = savedValue.value_text;
                }
              }

              return {
                id: crypto.randomUUID(),
                attributeId: ad.attribute_id,
                name: attr?.name || "",
                label: attr?.label || "",
                type: attr?.attribute_type || "text",
                isRequired: ad.is_required || attr?.is_required || false,
                options: attr?.options ? (attr.options as string[]) : null,
                value,
              };
            });

          return {
            id: cc.id,
            modelComponentId: cc.model_component_id,
            componentTypeId: componentTypeId || "",
            componentTypeName: componentType?.name || "",
            componentTypeLabel: modelComp?.label
              ? `${componentType?.label || ""} – ${modelComp.label}`
              : componentType?.label || "",
            materialId: cc.material_id,
            quantity: cc.quantity,
            minQuantity: modelComp?.min_quantity || 0,
            maxQuantity: modelComp?.max_quantity || 1,
            width: cc.width || null,
            height: cc.height || null,
            thickness: cc.thickness || null,
            allowedMaterials,
            attributes: attrDefs,
            sourceModelName: cc.source_model_name || undefined,
          };
        });

        const typedConfigData = configData as ConfigurationLoadResult;
        const productModelName = typedConfigData.product_models?.name || "";
        const isCustomTemplate = typedConfigData.product_models?.is_custom_template;
        
        // Jos tallennettu nimi eroaa mallin nimestä, se on kustomoitu nimi
        const customName = isCustomTemplate && typedConfigData.name && typedConfigData.name !== productModelName 
          ? typedConfigData.name 
          : undefined;

        setConfiguration({
          productModelId: typedConfigData.product_model_id,
          productModelName,
          customName,
          components: loadedComponents,
        });
        setConfigurationLineId(lineId);
      };

      loadOrder();
    } else if (open && !orderId) {
      form.reset({
        order_date: new Date(),
        order_type: "new_work",
        fulfillment_type: "installation",
        status: "draft",
      });
      setOrderLines([]);
      setOrderTrackingInfo(null);
      setOriginalStatus(null);
      setConfiguration(null);
      setConfigurationLineId(null);
    }
  }, [open, orderId, form, setOrderLines, setConfiguration, setConfigurationLineId]);

  return {
    isLoadingOrder,
    orderTrackingInfo,
    originalStatus,
    resetLoadOrderState,
  };
}
